import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Pagination } from "@/components/ui/pagination"
import { CompanyForm, emptyCompanyFormData, type CompanyFormData } from "@/components/CompanyForm"
import { CompanyTable } from "@/components/CompanyTable"
import { FlashMessages } from "@/components/FlashMessages"
import { SearchInput } from "@/components/SearchInput"
import { Plus, Truck } from "lucide-react"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"
import { formatCNPJ, formatPhone, stripMask } from "@/lib/masks"
import type { Company, SuppliersPageProps } from "@/types/company"

export default function Suppliers() {
  const { t } = useTranslation()
  const { auth, flash, suppliers, pagination, filters } = usePage<SuppliersPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Company | null>(null)
  const [searchValue, setSearchValue] = useState(filters.search)

  const createForm = useForm<CompanyFormData>({ ...emptyCompanyFormData })
  const editForm = useForm<CompanyFormData>({ ...emptyCompanyFormData })

  const debouncedSearch = useDebouncedCallback((value: string) => {
    router.get("/suppliers", {
      search: value || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }, 300)

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value)
    debouncedSearch(e.target.value)
  }

  function handleSort(column: string) {
    const newDirection = filters.sort === column && filters.direction === "asc" ? "desc" : "asc"
    router.get("/suppliers", {
      search: filters.search || undefined,
      sort: column,
      direction: newDirection,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }

  function goToPage(page: number) {
    router.get("/suppliers", {
      search: filters.search || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, { preserveState: true, preserveScroll: true })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Strip masks before submitting
    createForm.setData({
      ...createForm.data,
      tax_id: stripMask(createForm.data.tax_id),
      phone: stripMask(createForm.data.phone),
    })
    createForm.post("/suppliers", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingSupplier) return

    // Strip masks before submitting
    editForm.setData({
      ...editForm.data,
      tax_id: stripMask(editForm.data.tax_id),
      phone: stripMask(editForm.data.phone),
    })
    editForm.patch(`/suppliers/${editingSupplier.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingSupplier(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(supplier: Company) {
    setEditingSupplier(supplier)
    editForm.setData({
      name: supplier.name,
      tax_name: supplier.tax_name || "",
      tax_id: formatCNPJ(supplier.tax_id || ""),
      email: supplier.email || "",
      phone: formatPhone(supplier.phone || ""),
      street: supplier.street || "",
      city: supplier.city || "",
      state: supplier.state || "",
    })
  }

  function handleDelete(supplier: Company) {
    if (confirm(t("suppliers.confirmDelete"))) {
      router.delete(`/suppliers/${supplier.id}`)
    }
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("suppliers.title")}</h2>
            <p className="text-muted-foreground">{t("suppliers.subtitle")}</p>
          </div>

          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open)
            if (!open) createForm.reset()
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("suppliers.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("suppliers.addButton")}</DialogTitle>
                  <DialogDescription>{t("suppliers.addDescription")}</DialogDescription>
                </DialogHeader>
                <CompanyForm
                  data={createForm.data}
                  errors={createForm.errors}
                  onChange={(field, value) => createForm.setData(field, value)}
                  idPrefix="create"
                  translationPrefix="suppliers"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={!!editingSupplier} onOpenChange={(open) => !open && setEditingSupplier(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("suppliers.editSupplier")}</DialogTitle>
                  <DialogDescription>{t("suppliers.editDescription")}</DialogDescription>
                </DialogHeader>
                <CompanyForm
                  data={editForm.data}
                  errors={editForm.errors}
                  onChange={(field, value) => editForm.setData(field, value)}
                  idPrefix="edit"
                  translationPrefix="suppliers"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingSupplier(null)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              {t("suppliers.section")}
            </CardTitle>
            <CardDescription>{t("suppliers.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(suppliers.length > 0 || filters.search) && (
              <SearchInput
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={t("suppliers.searchPlaceholder")}
              />
            )}

            {suppliers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {filters.search ? t("suppliers.noSearchResults") : t("suppliers.noSuppliers")}
              </p>
            ) : (
              <>
                <CompanyTable
                  data={suppliers}
                  filters={filters}
                  onSort={handleSort}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                  translationPrefix="suppliers"
                />
                <Pagination pagination={pagination} onPageChange={goToPage} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
